// ----------------------------------------------------------------------------
//   Imports
// ----------------------------------------------------------------------------

// Custom Scripts: Local
import { sleep, getAPIBaseURL } from "../scripts/utils.js";
import { updateRecording } from "./backupRecordings.js";
import { ErrorState, trackError, UploadError, BatchUploadError, DBBackupError } from "./errorHandling.js";



// ----------------------------------------------------------------------------
//   Setup
// ----------------------------------------------------------------------------


const SERVER_URL = process.env.REACT_APP_SERVER_URL;


/** Minimum S3 multipart part size is 5 MB; use 10 MB for headroom. */
const MULTIPART_CHUNK_SIZE = 10 * 1024 * 1024;

/** Granular read size when scanning a blob for partial data recovery. */
const BLOB_SCAN_CHUNK_SIZE = 1 * 1024 * 1024;



// ----------------------------------------------------------------------------
//   Helper Functions
// ----------------------------------------------------------------------------


/**
 * Compute the SHA-256 hash of an ArrayBuffer and return it as a base64 string.
 * Used to generate the `x-amz-checksum-sha256` header for each uploaded part.
 * S3 verifies this server-side on receipt, rejecting any corrupted chunk.
 */
async function sha256Base64(arrayBuffer) {
  const hashBuffer = await crypto.subtle.digest('SHA-256', arrayBuffer);
  const hashBinary = Array.from(new Uint8Array(hashBuffer)).map(b => String.fromCharCode(b)).join('');
  return btoa(hashBinary);
}


/**
 * Read a blob in small increments, stopping at the first unreadable segment.
 * Any error other than NotFoundError is rethrown.
 *
 * Returns `{ buffers, truncated }`:
 *   - `buffers`:   ArrayBuffers covering the readable portion of the blob, in order.
 *   - `truncated`: true if reading stopped early due to a NotFoundError.
 */
async function scanBlobChunks( blob, scanChunkSize = BLOB_SCAN_CHUNK_SIZE ) {
  const buffers = [];
  let offset = 0;

  while (offset < blob.size) {
    const end   = Math.min(offset + scanChunkSize, blob.size);
    const chunk = blob.slice(offset, end);
    try {
      buffers.push(await chunk.arrayBuffer());
      offset = end;
    }
    catch (e) {
      if (e.name === 'NotFoundError') {
        // Blob data is unreadable beyond this point; return what we have
        return { buffers, truncated: true };
      }
      throw e;
    }
  }

  return { buffers, truncated: false };
}


/**
 * Concatenate an array of ArrayBuffers and split into upload-sized chunks.
 * Each returned ArrayBuffer is at most `uploadChunkSize` bytes.
 */
function aggregateBuffersToChunks( buffers, uploadChunkSize = MULTIPART_CHUNK_SIZE ) {

  // Concatenate all scan buffers into one contiguous block
  const totalBytes = buffers.reduce((sum, b) => sum + b.byteLength, 0);
  const combined   = new Uint8Array(totalBytes);
  let writeOffset  = 0;
  for (const buf of buffers) {
    combined.set(new Uint8Array(buf), writeOffset);
    writeOffset += buf.byteLength;
  }

  // Split into upload-sized slices
  const chunks = [];
  for (let start = 0; start < totalBytes; start += uploadChunkSize) {
    chunks.push(combined.buffer.slice(start, Math.min(start + uploadChunkSize, totalBytes)));
  }
  return chunks;
}


/**
 * Request a WakeLock for the screen, returning `null` if the request fails.
 */
async function getOptionalWakeLock() {

  // Request a WakeLock for the screen and return it
  try {
    return await navigator.wakeLock.request("screen");
  }

  // If the WakeLock request fails, skip it
  catch (err) {
    console.log(`Failed to obtain WakeLock: ${err.name}, ${err.message}`);
    return null;
  }
}



// ----------------------------------------------------------------------------
//   Uploads - Single Request
// ----------------------------------------------------------------------------


/**
 * Request an AWS signed URL from the API.
 * Returns an object containing:
 *   - `url`: The signed AWS URL to upload to
 *   - `filename`: The name of the file in S3.
 */
async function getUploadURL( recording ) {

  // Construct the route in the API
  const apiRoute = getAPIBaseURL("upload-url");

  // Add local file title, if specified
  if ( recording.title ) {
    apiRoute.searchParams.append("title", recording.title);
  }

  // Add content type, if specified
  if ( recording.blob?.type ) {
    apiRoute.searchParams.append("contentType", recording.blob.type);
  }
  else {
    apiRoute.searchParams.append("contentType", 'video/webm');
  }

  // Fetch signed URL from API
  try {
    const apiResponse = await fetch(
      apiRoute.toString(),
      {
        credentials: "include",
      }
    )

    if (!apiResponse.ok) {
      throw new Error('Unable to construct upload URL');
    }

    return await apiResponse.json();
  }

  // Intercept error, print where it came from, then propagate
  catch (e) {
    console.error('Unable to construct upload URL');
    throw e;
  }
}



// ----------------------------------------------------------------------------
//   Uploads - Multipart Requests
// ----------------------------------------------------------------------------


/**
 * Step 1: Initiate an S3 multipart upload.
 * Returns { uploadId, filename }.
 */
async function initiateMultipartUpload( recording, contentType ) {

  const apiRoute = getAPIBaseURL("multipart-upload/start");

  if ( recording.title ) {
    apiRoute.searchParams.append("title", recording.title);
  }

  apiRoute.searchParams.append("contentType", contentType || 'video/webm');

  const response = await fetch( apiRoute.toString(), { credentials: "include", method: 'POST' } );

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to initiate multipart upload: ${response.status} ${errorText}`);
  }

  return await response.json(); // { uploadId, filename }
}


/**
 * Step 2: Get a presigned URL for a single part.
 * Returns the signed URL string.
 */
async function getPartUploadURL( filename, uploadId, partNumber, checksum ) {

  const apiRoute = getAPIBaseURL("multipart-upload/part-url");
  apiRoute.searchParams.append("filename",   filename);
  apiRoute.searchParams.append("uploadId",   uploadId);
  apiRoute.searchParams.append("partNumber", partNumber);
  apiRoute.searchParams.append("checksum",   checksum);

  const response = await fetch( apiRoute.toString(), { credentials: "include" } );

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to get presigned URL for part ${partNumber}: ${response.status} ${errorText}`);
  }

  const { url } = await response.json();
  return url;
}


/**
 * Step 3: Complete the multipart upload.
 * `parts` is an array of { ETag, PartNumber }.
 * `truncated` signals that the uploaded data is a partial recovery of the original blob.
 * `computedSize` / `recoveredSize` are byte counts reported when truncated, stored as S3 tags.
 */
async function completeMultipartUpload( filename, uploadId, parts, truncated = false, computedSize = null, recoveredSize = null ) {

  const apiRoute = getAPIBaseURL("multipart-upload/complete");

  const response = await fetch( apiRoute.toString(), {
    method:      'POST',
    credentials: 'include',
    headers:     { 'Content-Type': 'application/json' },
    body:        JSON.stringify({ filename, uploadId, parts, truncated, computedSize, recoveredSize }),
  });

  if ( !response.ok ) {
    const errorText = await response.text().catch(() => response.statusText);
    throw new Error(`Failed to complete multipart upload: ${response.status} ${errorText}`);
  }
}


/**
 * Abort an in-progress multipart upload, freeing any uploaded parts.
 * Non-throwing — failure is logged but never blocks cleanup.
 */
async function abortMultipartUpload( filename, uploadId ) {

  const apiRoute = getAPIBaseURL("multipart-upload/abort");

  try {
    await fetch( apiRoute.toString(), {
      method:      'POST',
      credentials: 'include',
      headers:     { 'Content-Type': 'application/json' },
      body:        JSON.stringify({ filename, uploadId }),
    });
  }
  catch (err) {
    console.error('Failed to send multipart abort request:', err);
  }
}


/**
 * Upload a single blob chunk to a presigned S3 URL via XHR.
 * Returns the ETag from the S3 response (needed for CompleteMultipartUpload).
 *
 * NOTE: The S3 bucket's CORS config must include:
 *   - `ETag` in `ExposeHeaders` so the client can read the ETag from the response.
 *   - `x-amz-checksum-sha256` in `AllowedHeaders` so the client can send the checksum header.
 */
function uploadPart( url, chunk, partNumber, contentType, checksum, onProgress ) {
  return new Promise((resolve, reject) => {

    // Init request object & set basic parameters
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    if (contentType) xhr.setRequestHeader('Content-Type', contentType);
    // if (checksum)    xhr.setRequestHeader('x-amz-checksum-sha256', checksum);
    xhr.timeout = 5 * 60 * 1000; // 5 minutes per part

    // Attach the reject function to all the possible error events
    const handleErr = (event) => reject(event);
    xhr.addEventListener('abort',   handleErr);
    xhr.addEventListener('error',   handleErr);
    xhr.addEventListener('timeout', handleErr);

    // If a progress function was provided, attach it as well
    if (onProgress) {
      xhr.upload.addEventListener('progress', ({ loaded }) => onProgress(loaded));
    }

    // Main callback - completion
    // NOTE: Not guaranteed to be a success
    xhr.addEventListener('load', () => {

      // Check for error responses
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(`Part ${partNumber} upload failed with HTTP ${xhr.status}: ${ xhr.responseText || xhr.statusText }`));
        return;
      }

      // Retrieve the ETag from the response
      const etag = xhr.getResponseHeader('ETag');

      // If ETag is missing, the S3 bucket CORS config is likely missing ExposeHeaders: [ETag]
      if (!etag) {
        reject(new Error(`Part ${partNumber} response missing ETag header. Check S3 bucket CORS configuration.`));
        return;
      }

      resolve(etag);
    });

    // Upload the chunk
    xhr.send(chunk);
  });
}



// ----------------------------------------------------------------------------
//   Main Logic
// ----------------------------------------------------------------------------


/**
 * Request an AWS signed URL and upload a video blob to that URL.
 *
 * Args:
 *   - recording: The local database entry with the blob to upload
 *   - onProgress: An optional callback to run with progress updates.
 */
export async function uploadRecording(recording, onProgress, verbose=false) {
  if (verbose) console.log("Uploading...")

  const filetype = recording.blob?.type ?? 'video/webm';

  // Declare vars to make available in error
  let uploadId, filename;

  // If recording blob has no data, abort here
  if (verbose) console.log('Scanning blob for readable data...');
  if (!recording.blob) {
    throw new DBBackupError( recording, 'Recording has no blob.' );
  }
  if (!recording.blob.size) {
    throw new DBBackupError( recording, 'Recording blob has no size.' );
  }

  // Scan the blob in granular chunks, recovering as much data as possible.
  // NotFoundError on any individual chunk is caught; reading stops at the first
  // unreadable segment and truncated is set to true. Any other error propagates.
  const { buffers: scanBuffers, truncated } = await scanBlobChunks( recording.blob );
  if (scanBuffers.length === 0) {
    throw new DBBackupError( recording, 'Blob data is entirely unreadable (NotFoundError on first segment).' );
  }

  // Explicitly compute the size of the blob
  const computedSize  = recording.blob.size;
  const recoveredSize = scanBuffers.reduce((sum, b) => sum + b.byteLength, 0);

  if (verbose) {
    const readablePercent = (recoveredSize / computedSize * 100).toFixed(1);
    console.log(`Scanned blob: ${recoveredSize} of ${computedSize} bytes readable (~${readablePercent}%)${truncated ? ' — TRUNCATED' : ''}. Read in ${scanBuffers.length} buffers.`);
  }

  // Aggregate the granular scan buffers into upload-sized chunks
  const uploadChunks = aggregateBuffersToChunks( scanBuffers );

  // Try and get a wakelock (keeps the screen awake while uploading)
  const wakeLock = await getOptionalWakeLock();
  if (verbose) console.log(`Got wakelock: ${wakeLock}`)

  // Try the main logic
  // IMPORTANT: Any error should be thrown. Main catch block releases the wakelock.
  try {

    // ---- Step 1: Initiate ---------------------------------------------------

    // Get the destination
    ({ uploadId, filename } = await initiateMultipartUpload(recording, filetype));
    if (!uploadId) {
      throw new Error('Could not create / retrieve an uploadID.')
    }

    // Save filename to local IndexedDB; track failure with Sentry but don't block.
    try {
      await updateRecording( recording.recordingID, { filename } );
    }
    catch (e) {
      trackError(ErrorState.UPDATE_STORED_RECORDING_METADATA_FAILED, e);
    }


    // ---- Step 2: Upload parts -----------------------------------------------

    // Derive sizes from the pre-read upload chunks (may be less than blob.size if truncated)
    const numParts  = uploadChunks.length;
    const totalSize = uploadChunks.reduce((sum, buf) => sum + buf.byteLength, 0);
    if (verbose) console.log(`Total size: ${totalSize} bytes in ${numParts} part(s)${truncated ? ' [TRUNCATED]' : ''}.`);

    // { ETag, PartNumber } — required by CompleteMultipartUpload
    const parts     = [];

    // Track per-part loaded bytes for a single aggregated progress callback
    const progressByPart = new Array(numParts).fill(0);

    // Constructor to make callbacks for each part
    // Each part needs its own callback to update its own index
    const updatePartProgressCallback = (partNumber, totalSize) => {
      return (loaded) => {
        progressByPart[partNumber - 1] = loaded;
        if (onProgress) {
          onProgress(progressByPart.reduce((a, b) => a + b, 0), totalSize);
        }
      }
    }

    // Iterate through all parts
    // NOTE: AWS uses 1-indexing
    for (let partNumber = 1; partNumber <= numParts; partNumber++) {
      if (verbose) console.log(`Handling part ${partNumber}/${numParts}...`)

      // Retrieve the pre-read buffer for this part (no blob reads needed here)
      const chunkBuffer = uploadChunks[partNumber - 1];
      if (verbose) console.log(`Part size: ${ chunkBuffer.byteLength } bytes.`)

      // Compute SHA-256 checksum of the chunk before uploading.
      // S3 will verify this server-side on receipt and reject the part if it doesn't match.
      const checksum = await sha256Base64(chunkBuffer);
      if (verbose) console.log(`Computed checksum.`)

      // Fetch a fresh presigned URL for this part, propagating errors
      const partUrl = await getPartUploadURL( filename, uploadId, partNumber, checksum );
      if (verbose) console.log(`Constructed part upload URL.`)

      // Upload the chunk
      const etag = await uploadPart(
        partUrl, chunkBuffer, partNumber, filetype, checksum, updatePartProgressCallback(partNumber, totalSize)
      );
      if (verbose) console.log(`Part ${partNumber}/${numParts} uploaded! (ETag: ${etag})`);

      // Track the ETag and checksum — both required by CompleteMultipartUpload
      parts.push({ ETag: etag, PartNumber: partNumber, ChecksumSHA256: checksum });
    }


    // ---- Step 3: Complete ---------------------------------------------------

    // Try calling the completion endpoint
    await completeMultipartUpload(filename, uploadId, parts, truncated, computedSize, recoveredSize);
    if (verbose) console.log(`Uploaded successfully!${ truncated ? ' (truncated)' : '' }`);

    // Now that the upload is complete, we can release the wake lock
    if (wakeLock) {
      await wakeLock.release();
      if (verbose) console.log('Released wakelock.');
    } else {
      if (verbose) console.log('No wakelock to release.');
    }

    // Update local IndexedDB with uploaded status.
    // If this fails, log the error but do not fail the upload.
    try {
      await updateRecording(
        recording.recordingID,
        { uploaded: true, uploadedAt: new Date().toISOString() }
      );
      if (verbose) console.log('Updated local recording object to reflect new upload.')
    }
    catch (err) {
      console.error("Failed to mark recording as uploaded in local storage:", err);
    }

    // Return the name of the file in AWS, and whether the upload was truncated
    return { filename, truncated };
  }

  // If any error raised, release the wakelock and wrap in a tracked StateError
  catch (e) {
    if (verbose) console.error(`Raised error attempting to upload: ${e}.`)

    // If a wakelock was claimed, make sure to release it
    if (wakeLock) await wakeLock.release();

    // If an upload was started, make sure it's aborted
    if (uploadId) {
      if (verbose) console.log(`Aborting upload ${uploadId}`);
      abortMultipartUpload(filename, uploadId);
      if (verbose) console.log(`Aborted upload ${uploadId} successfully.`);
    }
    else {
      if (verbose) console.log(`No upload ID to abort.`)
    }

    // Wrap in an upload error, tracking "global" metadata about the recording itself
    throw new UploadError(e, {
      'recordingID':    recording.recordingID,
      'recordingSize':  recording.blob?.size,
      'recordingTitle': recording.title,
      'uploadID':       uploadId,
      'filenameAWS':    filename,
      'hasWakeLock':    wakeLock !== undefined && wakeLock !== null,
      'truncated':      truncated,
    })
  }
}


/**
 * Upload multiple recordings together.
 *
 * Each element in `recordings` will have the fields:
 *   - recordingID: unique ID in localdb
 *   - title: a local title for the file
 *   - blob: the actual object to upload
 *
 * @param {Array} recordings - Array of recording objects to upload
 * @param {Function} onProgress - Callback for progress updates (loaded, total)
 * @returns {Promise<Array>} - Promise that resolves with an array of results from each upload when all uploads succeed.
 * @throws {StateError} If one or more uploads fail.
 */
export async function uploadRecordingsBatch(recordings, onProgress, verbose = false) {
  if (verbose) console.debug('Uploading recordings: ', recordings )

  // Compute the combined total size of all blobs
  const totalSize = recordings.reduce((sum, recording) => {
    return sum + (recording.blob?.size || 0);
  }, 0);

  // Warn if computed size is 0 (or negative somehow)
  if (totalSize <= 0) {
    console.warn(`Computed total upload size as ${totalSize} bytes.`)
  } else {
    if (verbose) console.log('Total size: ', totalSize)
  }

  // Track progress for each upload
  const progressByRecording = new Map();
  recordings.forEach(recording => {
    progressByRecording.set(recording.recordingID, 0);
  });

  // Helper function to update aggregated progress
  // Note: If totalSize was computed at 0, sends a progress update indicating half the data is loaded.
  // TODO: Smarter totalSize computation based on individual upload calls
  const updateAggregatedProgress = () => {
    const totalLoaded = Array.from(progressByRecording.values()).reduce((sum, loaded) => sum + loaded, 0);
    if (onProgress) {
      onProgress(totalLoaded, totalSize <= 0 ? (totalLoaded * 2) : totalSize);
    }
  };

  // Create upload promises for each recording
  const uploadPromises = recordings.map((recording) => async () => {

    // Create a progress handler for this specific recording
    const recordingProgressHandler = (loaded, total) => {

      // Update this recording's progress
      progressByRecording.set(recording.recordingID, loaded);

      // Update the aggregated progress
      updateAggregatedProgress();
    };

    // Upload the recording
    try {
      const result = await uploadRecording(recording, recordingProgressHandler, verbose);
      if (verbose) console.log(`Successfully uploaded recording ${recording.recordingID}`)

      return {
        success: true,
        recordingId: recording.recordingID,
        filename: result?.filename,
        error: null,
      };
    }
    catch (error) {
      console.error(`Failed to upload recording ${recording.recordingID}:`, error);

      return {
        success: false,
        recordingId: recording.recordingID,
        filename: null,
        error: error,
      };
    }
  });

  // Execute uploads sequentially
  // TODO: Could potentially speed up with parallel execution, but we'd have to ensure the server assigns distinct names to all the files
  const uploadResults = [];
  for (const uploadPromise of uploadPromises) {
    uploadResults.push(await uploadPromise());
  }
  // TODO: Consider clearing successfully uploaded recordings from local storage
  // after successful upload. This would prevent re-uploading the same files.

  // Aggregate errors across all the uploads
  const failedUploads = uploadResults.filter(({success}) => !success);
  if (failedUploads.length) {
    throw new BatchUploadError( uploadResults );
  }

  // Return the set of results
  return uploadResults;
}



// ----------------------------------------------------------------------------
//   Testing
// ----------------------------------------------------------------------------


/**
 * Drop-in replacement for `uploadRecording` that simply reports
 * progress updates, without performing any actual upload logic.
 *
 * Useful for testing workflow without requiring an actual blob to upload.
 */
export async function spoofUploadRecording( recording, onProgress, throwError = false ) {

  // If an actual blob was passed, log it
  if (recording !== null) {
    console.log("Received recording for upload: ", recording)
  }

  // Parameters
  const numIncrements = 3;
  const delayMS       = 1000;

  // Loop and wait, simulating upload behavior
  for ( let i = 1; i <= numIncrements; i++ ) {
    await sleep( delayMS );
    onProgress( i, numIncrements );

    // Optionally throw an error midway through "uploading"
    if ( throwError && i >= 2 ) throw new Error('Dev error!');
  }

  // Final wait
  await sleep( delayMS );
}
